import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import VetrinaCard from './components/VetrinaCard';
import { Search, Filter } from 'lucide-react';

const PublicVetrine = () => {
  const [vetrine, setVetrine] = useState([]);
  const [filteredVetrine, setFilteredVetrine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoria, setSelectedCategoria] = useState('all');
  const [selectedZona, setSelectedZona] = useState('all');
  const [categorie, setCategorie] = useState([]);
  const [zone, setZone] = useState([]);
  const [showDemo, setShowDemo] = useState(false);

  useEffect(() => {
    loadVetrine();
    loadFilters();
  }, []);

  useEffect(() => {
    filterVetrine();
  }, [vetrine, searchTerm, selectedCategoria, selectedZona, showDemo]);

  const loadVetrine = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        ?.from('vetrine_stores')
        ?.select('*')
        ?.eq('status', 'attiva')
        ?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setVetrine(data || []);
    } catch (err) {
      console.error('Errore caricamento vetrine:', err);
      setError(err?.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const loadFilters = async () => {
    try {
      const [categorieRes, zoneRes] = await Promise.all([
        supabase?.from('vetrine_categories')?.select('value, label')?.order('label'),
        supabase?.from('vetrine_zones')?.select('value, label')?.order('label')
      ]);

      if (categorieRes?.data) setCategorie(categorieRes?.data);
      if (zoneRes?.data) setZone(zoneRes?.data);
    } catch (err) {
      console.error('Errore caricamento filtri:', err);
    }
  };

  const filterVetrine = () => {
    let filtered = [...vetrine];

    if (searchTerm) {
      const search = searchTerm?.toLowerCase();
      filtered = filtered?.filter(v => 
        v?.nome_negozio?.toLowerCase()?.includes(search) ||
        v?.descrizione_breve?.toLowerCase()?.includes(search) ||
        v?.categoria?.toLowerCase()?.includes(search) ||
        v?.zona?.toLowerCase()?.includes(search)
      );
    }

    if (selectedCategoria !== 'all') {
      filtered = filtered?.filter(v => v?.categoria === selectedCategoria);
    }

    if (selectedZona !== 'all') {
      filtered = filtered?.filter(v => v?.zona === selectedZona);
    }

    setFilteredVetrine(filtered);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento vetrine...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 font-semibold">Errore</p>
          <p className="text-gray-600 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Vetrine Pubbliche</h1>
          <p className="text-gray-600">Scopri tutti i negozi e le attività della tua zona</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Cerca per nome, categoria o zona..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <select
                value={selectedCategoria}
                onChange={(e) => setSelectedCategoria(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Tutte le categorie</option>
                {categorie?.map((cat) => (
                  <option key={cat?.value} value={cat?.value}>
                    {cat?.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <select
                value={selectedZona}
                onChange={(e) => setSelectedZona(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Tutte le zone</option>
                {zone?.map((zona) => (
                  <option key={zona?.value} value={zona?.value}>
                    {zona?.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            {filteredVetrine?.length === vetrine?.length ? (
              <span>Totale vetrine: <strong>{vetrine?.length}</strong></span>
            ) : (
              <span>
                Mostrando <strong>{filteredVetrine?.length}</strong> di <strong>{vetrine?.length}</strong> vetrine
              </span>
            )}
          </div>
        </div>

        {filteredVetrine?.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <Filter className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">
              {searchTerm || selectedCategoria !== 'all' || selectedZona !== 'all' ?'Nessuna vetrina trovata con i filtri selezionati' :'Nessuna vetrina disponibile'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVetrine?.map((vetrina) => (
              <VetrinaCard key={vetrina?.id} vetrina={vetrina} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicVetrine;